/**
 * A drafted reply, offered to the agent — never to the customer.
 *
 * ## Why it is offered rather than sent
 *
 * The assistant answers on its own only while it is the one handling the
 * conversation. Once a person has joined, everything the customer sees comes from
 * that person, and this does not change it: nothing here reaches the transcript
 * until an agent presses Send, and what arrives is attributed to them because they
 * sent it. The customer is never shown a machine reply dressed as a human one.
 *
 * Three actions, and doing nothing is a fourth. **Send** puts it out as written.
 * **Edit** moves it into the message box, which is the honest place for "nearly
 * right" — the agent finishes the sentence themselves. **Ignore** dismisses it.
 * Leaving it alone leaves it on screen: an agent mid-thought should not have a
 * suggestion vanish from under them on a timer.
 *
 * ## Why it can be stale, and why that is fine
 *
 * A suggestion answers one particular customer message. If the customer writes
 * again before the agent acts, this is replaced by one that answers the newer
 * message — the older draft was a reply to a question that has moved on.
 */

interface Suggestion {
  readonly text: string;
  /** The customer message this answers. Replaced when a newer one arrives. */
  readonly forMessageId: string;
  /**
   * Set when the guardrails would have refused to send this to a customer.
   *
   * Shown rather than hidden. The knowledge base may legitimately contain dosing
   * information that an agent needs to read and must not paste — and an agent told
   * *why* it is flagged can make that call, where a silently-withheld draft leaves
   * them wondering why the assistant has nothing to say.
   */
  readonly blockedForCustomer?: string;
}

export function SuggestedReply({
  suggestion,
  busy,
  onSend,
  onEdit,
  onIgnore,
}: {
  /** Null while nothing is drafted, or once the agent has dealt with it. */
  readonly suggestion: Suggestion | null;
  readonly busy: boolean;
  readonly onSend: () => void;
  readonly onEdit: () => void;
  readonly onIgnore: () => void;
}) {
  if (suggestion === null) return null;

  return (
    <div className="suggestion" role="region" aria-label="Suggested reply">
      <div className="suggestion-head">
        <span className="tag">Suggested reply</span>
        {suggestion.blockedForCustomer !== undefined && (
          <span className="tag warn" title="The assistant would not send this to a customer itself">
            Check before sending · {suggestion.blockedForCustomer.replaceAll("_", " ")}
          </span>
        )}
      </div>

      <p className="suggestion-text">{suggestion.text}</p>

      {/*
        The visible words stay short, because the card above them already says what
        they act on. The accessible names do not: this sits directly above the
        message box, whose own button is also called "Send", and two buttons called
        the same thing a few pixels apart is exactly the ambiguity a screen reader
        cannot resolve from position.
      */}
      <div className="row">
        <button
          className="primary"
          disabled={busy}
          aria-label="Send the suggested reply"
          onClick={onSend}
        >
          Send
        </button>
        <button disabled={busy} aria-label="Edit the suggested reply" onClick={onEdit}>
          Edit
        </button>
        <button disabled={busy} aria-label="Ignore the suggested reply" onClick={onIgnore}>
          Ignore
        </button>
      </div>
    </div>
  );
}

export type { Suggestion };
